import { Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { Vertical, VerticalDepth } from '@shared/models';
import { MerchantsApiService } from '@app/areas/merchants/services/merchants.api.service';

type MerchantVerticalsCardMode = 'view' | 'edit';

type PrimaryVerticalOption = { VerticalId: number | null; Name: string };

const NONE_PRIMARY_VERTICAL_OPTION: PrimaryVerticalOption = { VerticalId: null, Name: 'None' };

@Component({
  selector: 'app-merchant-verticals-card',
  templateUrl: './merchant-verticals-card.component.html',
  styleUrls: ['./merchant-verticals-card.component.scss'],
  standalone: true,
  imports: [ButtonModule, ChipModule, DropdownModule, FormsModule, NgFor, NgIf],
})
export class MerchantVerticalsCardComponent implements OnChanges, OnDestroy {
  private readonly merchantsApiService = inject(MerchantsApiService);
  private readonly messageService = inject(MessageService);

  @Input({ required: true }) merchantId!: number;

  protected mode: MerchantVerticalsCardMode = 'view';
  protected isLoading = false;
  protected isSaving = false;
  protected primaryVertical: Vertical | null = null;
  protected selectedPrimaryVerticalId: number | null = null;
  protected subVerticals: Vertical[] = [];
  protected primaryVerticalOptions: PrimaryVerticalOption[] = [];
  protected availableSubVerticals: Vertical[] = [];
  protected subVerticalToAdd: number | null = null;

  private committedSubVerticals: Vertical[] = [];
  private subs = new Subscription();

  protected get canAddSubVertical(): boolean {
    return this.selectedPrimaryVerticalId !== null && this.subVerticals.length === 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['merchantId']) {
      this.mode = 'view';
      this.loadMerchantVerticals();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected edit(): void {
    this.selectedPrimaryVerticalId = this.primaryVertical?.VerticalId ?? null;
    this.subVerticals = [...this.committedSubVerticals];
    this.subVerticalToAdd = null;
    this.mode = 'edit';

    if (this.primaryVerticalOptions.length === 0) {
      this.subs.add(
        this.merchantsApiService.getAllVerticals().subscribe((verticals) => {
          this.primaryVerticalOptions = [
            NONE_PRIMARY_VERTICAL_OPTION,
            ...verticals.filter((vertical) => vertical.Depth === VerticalDepth.Primary),
          ];
          this.availableSubVerticals = verticals.filter((vertical) => vertical.Depth === VerticalDepth.Sub);
        })
      );
    }
  }

  protected onPrimaryVerticalChange(primaryVerticalId: number | null): void {
    if (primaryVerticalId === null) {
      this.subVerticals = [];
    }
  }

  protected removeSubVertical(verticalId: number): void {
    this.subVerticals = this.subVerticals.filter((subVertical) => subVertical.VerticalId !== verticalId);
  }

  protected addSubVertical(verticalId: number | null): void {
    const subVertical = this.availableSubVerticals.find(
      (availableSubVertical) => availableSubVertical.VerticalId === verticalId
    );
    if (subVertical) {
      this.subVerticals = [subVertical];
    }

    this.subVerticalToAdd = null;
  }

  protected cancel(): void {
    this.subVerticals = this.committedSubVerticals;
    this.mode = 'view';
  }

  protected save(): void {
    this.isSaving = true;

    this.subs.add(
      this.merchantsApiService
        .saveVerticals(this.merchantId, {
          PrimaryVerticalId: this.selectedPrimaryVerticalId,
          SubVerticalIds: this.subVerticals.map((subVertical) => subVertical.VerticalId),
        })
        .subscribe((verticals) => {
          this.isSaving = false;
          if (verticals) {
            this.applyMerchantVerticals(verticals);
            this.mode = 'view';
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: Merchant Verticals',
              detail: 'Something went wrong while saving merchant verticals',
            });
          }
        })
    );
  }

  private loadMerchantVerticals(): void {
    this.isLoading = true;
    this.subs.add(
      this.merchantsApiService.getVerticalsByMerchantId(this.merchantId).subscribe((verticals) => {
        this.isLoading = false;
        this.applyMerchantVerticals(verticals);
      })
    );
  }

  private applyMerchantVerticals(verticals: Vertical[]): void {
    this.primaryVertical = verticals.find((vertical) => vertical.Depth === VerticalDepth.Primary) ?? null;
    this.committedSubVerticals = verticals.filter((vertical) => vertical.Depth === VerticalDepth.Sub);
    this.subVerticals = this.committedSubVerticals;
  }
}
